import React, { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { WordBlock } from '../components/WordBlock';
import { evaluateSentenceAttempt } from '../../../../services/answerMatchingEngine';
import { learnerProgressService } from '../../../../services/learnerProgressService';
import { useUserStore } from '../../../../stores/useUserStore';
import { renderParsedText } from '../../../../components/common/HoverWord';
import { CheckCircle2, AlertCircle, RefreshCw, Sparkles, ArrowRight, Trophy, BookOpen, Timer, Flame, Play, Pause, RotateCcw } from 'lucide-react';
import LearnGrammarModal from '../../../../components/cambridge/LearnGrammarModal';
import { fireCelebrationConfetti } from '../../../../utils/confettiHelper';
import { playButtonClick, playCorrectSound, playWrongSound, playVictoryFanfare } from '../../../../utils/soundEffects';

export function SentenceBuilderBattle({ customDrills, grammarDrills, drills, weekNumber, grammarLesson, onAttemptResult, onComplete }) {
  const currentUser = useUserStore((state) => state.currentUser);
  const learnerId = currentUser?.id || currentUser?.username || 'guest_01';
  const [showGrammarModal, setShowGrammarModal] = useState(false);

  const [gameState, setGameState] = useState('idle'); // 'idle' | 'playing' | 'paused' | 'gameover'
  const [currentDrillIndex, setCurrentDrillIndex] = useState(0);
  const [targetBlocks, setTargetBlocks] = useState([]);
  const [bankBlocks, setBankBlocks] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [timeLeft, setTimeLeft] = useState(45);

  const inputDrills = customDrills || grammarDrills || drills;
  const drillsList = (inputDrills && Array.isArray(inputDrills) && inputDrills.length > 0)
    ? inputDrills
    : null;

  const currentDrill = drillsList ? (drillsList[currentDrillIndex] || drillsList[0]) : null;
  const totalDrillsCount = drillsList ? drillsList.length : 0;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 100, tolerance: 5 } })
  );

  // Load drill word blocks
  const loadDrill = (idx) => {
    if (!drillsList || drillsList.length === 0) return;
    const drill = drillsList[idx] || drillsList[0];
    if (!drill) return;

    const correctWords = Array.isArray(drill.word_blocks) ? drill.word_blocks : [];
    const distractors = Array.isArray(drill.distractor_blocks) ? drill.distractor_blocks : [];
    const allWords = [...correctWords, ...[...distractors].sort(() => Math.random() - 0.5)];

    const shuffled = allWords
      .map((word, index) => ({ id: `block-${index}-${word}`, word }))
      .sort(() => Math.random() - 0.5);

    setBankBlocks(shuffled);
    setTargetBlocks([]);
    setFeedback(null);
  };

  // Start Game
  const handleStartGame = () => {
    setCurrentDrillIndex(0);
    setScore(0);
    setStreak(0);
    setCorrectCount(0);
    setTimeLeft(45);
    setGameState('playing');
    loadDrill(0);
  };

  const handleTogglePause = () => {
    setGameState(prev => (prev === 'playing' ? 'paused' : 'playing'));
  };

  const finishGame = () => {
    setGameState('gameover');
    const xpEarned = score > 0 ? 35 : 0; // Anti-cheat: 0 XP if AFK!

    if (score > 0) {
      playVictoryFanfare();
      fireCelebrationConfetti('SentenceSmash_Victory');
    }

    if (onComplete) onComplete(score);
  };

  // Timer Engine (Linear 1s countdown, independent of score changes)
  useEffect(() => {
    if (gameState !== 'playing') return;

    const timer = setInterval(() => {
      setTimeLeft(prev => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [gameState]);

  // Timeout trigger when timeLeft hits 0
  useEffect(() => {
    if (timeLeft === 0 && gameState === 'playing') {
      finishGame();
    }
  }, [timeLeft, gameState]);

  const handleDragStart = (event) => setActiveId(event.active.id);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over || gameState !== 'playing') return;

    if (active.id !== over.id) {
      const activeInTarget = targetBlocks.some((b) => b.id === active.id);
      const overInTarget = targetBlocks.some((b) => b.id === over.id);

      if (activeInTarget && overInTarget) {
        setTargetBlocks((items) => {
          const oldIndex = items.findIndex((i) => i.id === active.id);
          const newIndex = items.findIndex((i) => i.id === over.id);
          return arrayMove(items, oldIndex, newIndex);
        });
      }
    }
  };

  const handleBlockClick = (block) => {
    if (gameState !== 'playing') return;
    playButtonClick();
    const inTarget = targetBlocks.some((b) => b.id === block.id);
    if (inTarget) {
      setTargetBlocks((prev) => prev.filter((b) => b.id !== block.id));
      setBankBlocks((prev) => [...prev, block]);
    } else {
      setBankBlocks((prev) => prev.filter((b) => b.id !== block.id));
      setTargetBlocks((prev) => [...prev, block]);
    }
  };

  const handleCheckAnswer = () => {
    if (gameState !== 'playing') return;
    const userSentence = targetBlocks.map((b) => b.word).join(' ');
    const evalResult = evaluateSentenceAttempt(targetBlocks.map(b => b.word), currentDrill);

    if (evalResult.isCorrect) {
      playCorrectSound();
      const nextStreak = streak + 1;
      const nextCorrect = correctCount + 1;
      setStreak(nextStreak);
      setCorrectCount(nextCorrect);
      const bonusScore = 20 + nextStreak * 5;
      setScore(prev => prev + bonusScore);
      setTimeLeft(prev => Math.min(45, prev + 3));

      setFeedback({
        isCorrect: true,
        text: `🎉 Excellent! Correct sentence structure (+${bonusScore} PTS)`
      });

      setTimeout(() => {
        if (currentDrillIndex + 1 < totalDrillsCount) {
          const nextIdx = currentDrillIndex + 1;
          setCurrentDrillIndex(nextIdx);
          loadDrill(nextIdx);
        } else {
          finishGame();
        }
      }, 1000);
    } else {
      playWrongSound();
      setStreak(0);
      setFeedback({
        isCorrect: false,
        text: evalResult.feedbackText || 'Incorrect order. Re-arrange word blocks and try again!'
      });
    }
  };

  // Instruction in pure English — zero Vietnamese sentence prompt!
  const drillInstruction = 'Arrange the word blocks to build the correct sentence:';

  if (!drillsList || drillsList.length === 0) {
    return (
      <div data-testid="grammar-duel-data-missing" className="w-full max-w-7xl mx-auto p-8 bg-rose-50 border-2 border-rose-300 rounded-3xl text-center space-y-3 shadow-inner font-sans">
        <AlertCircle size={48} className="mx-auto text-rose-500 animate-pulse" />
        <h3 className="text-xl font-black text-rose-900">⚠️ Grammar Duel Data Missing</h3>
        <p className="text-sm font-semibold text-rose-700">
          No grammar drills found for Week {weekNumber || 'N/A'}. Expected data in skill_practice_hub.grammar_drills.
        </p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-7xl mx-auto p-4 sm:p-6 lg:p-7 bg-white rounded-2xl sm:rounded-3xl border-2 border-indigo-200 shadow-xl space-y-4 text-slate-900 font-sans">
      {/* Header Info */}
      <div className="flex items-center justify-between pb-3 border-b border-slate-200 flex-wrap gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center font-black text-xl shadow-sm shrink-0">
            🧱
          </div>
          <div className="min-w-0">
            <div className="text-[11px] sm:text-xs font-black text-indigo-600 uppercase tracking-wider">
              Grammar Duel • Round {currentDrillIndex + 1}/{totalDrillsCount}
            </div>
            <h3 className="text-sm sm:text-base lg:text-lg font-black text-slate-900 truncate">
              {drillInstruction}
            </h3>
          </div>
        </div>

        <div className="flex items-center gap-1.5 flex-wrap">
          {grammarLesson && (
            <button
              onClick={() => setShowGrammarModal(true)}
              className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 text-white text-xs font-black rounded-xl shadow-sm flex items-center gap-1 transition active:scale-95"
            >
              <BookOpen size={13} className="text-amber-300" /> 📘 Rules
            </button>
          )}

          {gameState === 'playing' && (
            <button
              type="button"
              onClick={handleTogglePause}
              className="p-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg border border-slate-300 transition"
              title="Pause Timer"
            >
              <Pause size={14} />
            </button>
          )}

          {gameState === 'paused' && (
            <button
              type="button"
              onClick={handleTogglePause}
              className="px-2.5 py-1 bg-emerald-600 text-white rounded-lg font-black text-[11px] flex items-center gap-1 shadow-sm"
            >
              <Play size={12} /> Resume
            </button>
          )}

          <div className="px-2.5 py-1 bg-slate-100 rounded-lg border border-slate-200 flex items-center gap-1 font-mono text-[11px] font-black">
            <Timer className={timeLeft <= 8 && gameState === 'playing' ? 'text-rose-500 animate-ping' : 'text-indigo-600'} size={13} />
            <span className={timeLeft <= 8 ? 'text-rose-600' : 'text-slate-900'}>{timeLeft}s</span>
          </div>

          <div data-testid="grammar-score-display" className="px-2.5 py-1 bg-amber-100 text-amber-900 rounded-lg border border-amber-300 font-mono text-[11px] font-black">
            {score} PTS
          </div>

          <span className="px-2 py-1 bg-indigo-50 text-indigo-900 text-[11px] font-mono font-bold rounded-lg border border-indigo-200">
            {currentDrillIndex + 1}/{totalDrillsCount}
          </span>
        </div>
      </div>

      {/* Start Screen (Idle) */}
      {gameState === 'idle' && (
        <div className="p-5 sm:p-6 bg-indigo-50/80 border-2 border-indigo-200 rounded-2xl text-center space-y-3 shadow-inner">
          <div className="w-11 h-11 rounded-2xl bg-indigo-600 text-white flex items-center justify-center font-black text-2xl mx-auto shadow-md">
            🧱
          </div>
          <div className="space-y-1">
            <h3 className="text-base sm:text-lg font-black text-slate-900 leading-tight">
              READY FOR<br />SENTENCE SMASH?
            </h3>
          </div>
          <button
            type="button"
            onClick={handleStartGame}
            className="px-6 py-2.5 sm:py-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 text-white rounded-xl font-black text-sm shadow-lg inline-flex items-center gap-1.5 transition hover:scale-105 active:scale-95"
          >
            <Play size={18} fill="currentColor" /> ▶ START
          </button>
        </div>
      )}

      {/* Paused Banner */}
      {gameState === 'paused' && (
        <div className="p-3 bg-indigo-50 border border-indigo-300 rounded-2xl flex items-center justify-between animate-in fade-in">
          <span className="text-xs font-black text-indigo-900 flex items-center gap-2">
            <Pause size={16} className="text-indigo-600 animate-pulse" /> Timer Paused — Re-order word blocks at your own pace!
          </span>
          <button
            type="button"
            onClick={handleTogglePause}
            className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-black text-xs shadow-md flex items-center gap-1"
          >
            <Play size={14} fill="currentColor" /> Resume
          </button>
        </div>
      )}

      {/* Game Over Screen */}
      {gameState === 'gameover' && (
        <div className="p-8 bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-300 rounded-3xl text-center space-y-4 shadow-inner animate-in zoom-in-95">
          <Trophy size={56} className="mx-auto text-indigo-600 animate-bounce" />
          <h3 className="text-2xl font-black text-slate-900">
            {score > 0 ? 'SENTENCE SMASH COMPLETE!' : 'TIME EXPIRED — TRY AGAIN!'}
          </h3>
          <div className="flex items-center justify-center gap-6 text-sm font-bold text-slate-700">
            <div>Score: <span className="text-xl font-black text-indigo-600">{score} PTS</span></div>
            <div>Sentences: <span className="text-xl font-black text-emerald-600">{correctCount}/{totalDrillsCount}</span></div>
            <div>XP Earned: <span className={`text-xl font-black ${score > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>+{score > 0 ? 35 : 0} XP</span></div>
          </div>
          <button
            type="button"
            onClick={handleStartGame}
            className="px-6 py-3.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 text-white rounded-2xl font-black text-sm shadow-xl inline-flex items-center gap-2 transition hover:scale-105"
          >
            <RotateCcw size={18} /> Play Sentence Smash Again
          </button>
        </div>
      )}

      {/* Active Game Display */}
      {(gameState === 'playing' || gameState === 'paused') && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <div className="space-y-3">
            {/* Target Drop Zone Area */}
            <div className="p-3 sm:p-4 bg-indigo-50/70 border-2 border-dashed border-indigo-300 rounded-2xl min-h-[60px] sm:min-h-[72px] flex flex-wrap items-center gap-2 shadow-inner">
              {targetBlocks.length > 0 && (
                <SortableContext items={targetBlocks.map((b) => b.id)} strategy={horizontalListSortingStrategy}>
                  {targetBlocks.map((block) => (
                    <WordBlock key={block.id} id={block.id} word={block.word} onClick={() => handleBlockClick(block)} />
                  ))}
                </SortableContext>
              )}
            </div>

            {/* Word Bank Dock */}
            <div className="p-3 sm:p-4 bg-slate-50 border border-slate-200 rounded-2xl">
              <div className="flex flex-wrap gap-2">
                {bankBlocks.map((block) => (
                  <WordBlock key={block.id} id={block.id} word={block.word} onClick={() => handleBlockClick(block)} />
                ))}
              </div>
            </div>

            {/* Action Bar */}
            <div className="flex items-center justify-between flex-wrap gap-3 pt-2">
              <button
                type="button"
                data-testid="grammar-check-btn"
                onClick={handleCheckAnswer}
                disabled={targetBlocks.length === 0}
                className="px-7 py-3 sm:py-3.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white font-black rounded-xl text-sm sm:text-base shadow-md flex items-center gap-2 transition active:scale-95 cursor-pointer"
              >
                <Sparkles size={18} /> Check Sentence
              </button>

              {feedback && (
                <div data-testid="grammar-feedback" className={`p-3 sm:p-3.5 rounded-xl border text-xs sm:text-sm font-black flex items-center gap-2 animate-in fade-in ${
                  feedback.isCorrect ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'bg-rose-50 border-rose-300 text-rose-800'
                }`}>
                  {feedback.isCorrect ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
                  {feedback.text}
                </div>
              )}
            </div>
          </div>
        </DndContext>
      )}

      {showGrammarModal && grammarLesson && (
        <LearnGrammarModal
          isOpen={showGrammarModal}
          onClose={() => setShowGrammarModal(false)}
          grammarLesson={grammarLesson}
        />
      )}
    </div>
  );
}

export default SentenceBuilderBattle;
